/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import {
  CartButtonWithQuantitySelector,
  Link,
  ProductImage,
  smallProductImageUrl,
  Rating,
  Icon,
  productLinkBuilder,
  priceAndPointConverter,
} from 'common-ui';

import type { ProductPropsTransformed } from 'tsconfig/types';
import { useDisclosure } from '@mantine/hooks';
import { css, useTheme } from '@emotion/react';
import Zoom from './Zoom';
import { ProductCardSkeleton } from '../Skeleton/ProductCard/ProductCard';
import { storeQueryIDForProduct } from '../../utils/algoliaQueryIDStorage';
import { PricePerLitre } from '../PricingAndPurchasePanel/components/Shared/PricePerLitre';

interface ItemCardProps {
  product: any;
  showRating?: boolean;
  showImageZoom?: boolean;
  hideBackgroundColour?: boolean;
  showPrice?: boolean;
  showAddtoCart?: boolean;
  queryID?: string;
  isBot?: boolean;
}

const productData = (item: ProductPropsTransformed) => ({
  productName: item.productName ?? '',
  vintage: item.vintage ?? '',
  productSlug: item.productSlug ?? '',
  itemCode: item.itemCode ?? '',
  giftFlag: item.giftFlag ?? false,
  avgRating: item.avgRating,
  skus: item.skus,
  packaging: item.packaging ?? '',
  productType: item.productType ?? '',
  productWebType: item.productWebType ?? '',
  largeImage: item.largeImage ?? '',
  price: item?.skus?.[0]?.vppPrice ? item?.skus?.[0]?.vppPrice : item?.skus?.[0]?.salePrice,
  buyersRRP: item?.skus?.[0]?.buyersRRP,
  pricePerLitre: item?.skus?.[0]?.vppPrice ? item?.skus?.[0]?.vppPricePerLitre : item?.skus?.[0]?.salePricePerLitre,
});

/**
 * Renders a card component for displaying an item.
 *
 * @example
 * ```tsx
 * <ItemCard product={productData} showRating={true} showImageZoom={true} hideBackgroundColour={false} />
 * ```
 *
 * @param {any} product - The product data for the item.
 * @param {boolean} [showRating=false] - Whether to show the rating for the item.
 * @param {boolean} [showImageZoom=false] - Whether to show the image zoom functionality for the item.
 * @param {boolean} [hideBackgroundColour=false] - Whether to hide the background color of the item card.
 * @param {boolean} [showPrice=true] - Whether to hide the Price for the item.
 * @param {boolean} [showAddtoCart=true] - Whether to hide the Add to Cart button of the item card.
 * @returns {JSX.Element} The rendered ItemCard component.
 */
export function ItemCard(props: Readonly<ItemCardProps>): React.JSX.Element {
  const {
    product,
    showRating = false,
    showImageZoom = false,
    hideBackgroundColour = false,
    showPrice = true,
    showAddtoCart = true,
  } = props;

  const item = productData(product);
  const [opened, { open, close }] = useDisclosure(false);
  const isLoadingProductItem = item === undefined;
  const theme = useTheme();

  const wrapper = css`
    ${theme.widgets.Product?.ItemCard}
  `;

  const handleOnClose = () => {
    close();
  };

  // Store queryID on hover to capture all click types (including right-click → new tab)
  const handleProductHover = () => {
    storeQueryIDForProduct(product?.itemCode, product?.__queryID, product?.__position || 0, product?.__indexName || '');
  };

  // Clean URL without queryID parameter
  const productUrl = productLinkBuilder(item.productSlug, item.itemCode);

  return (
    <div css={wrapper}>
      {isLoadingProductItem ? (
        <ProductCardSkeleton />
      ) : (
        <>
          {showImageZoom && (
            <span
              data-testid="zoom-container"
              className="zoom"
              onClick={() => open()}
              onKeyDown={() => open()}
              tabIndex={0}
              role="button"
            >
              <Icon kind="zoom" size="xs" />
            </span>
          )}

          <div className="card-container">
            <div
              className={`${
                hideBackgroundColour ? 'product-image-container no-background' : 'product-image-container'
              }`}
            >
              <div className="product-image" onMouseEnter={handleProductHover} onClick={handleProductHover}>
                <Link href={productUrl}>
                  <ProductImage
                    src={smallProductImageUrl(item.itemCode)}
                    productType={item?.productType}
                    alt={item.productName}
                    fill={true}
                  />
                </Link>
              </div>
            </div>

            <div className="info-container">
              <div className="product-name" onMouseEnter={handleProductHover} onClick={handleProductHover}>
                <Link href={productUrl}>
                  {item.productName} {item.vintage}
                </Link>
              </div>

              {showRating && (
                <div data-testid="rating-container" className="rating-container">
                  <Rating rating={item.avgRating} />
                </div>
              )}

              {showPrice && (
                <div className="price">
                  <span className="sale-price">{priceAndPointConverter(item.price)}</span>{' '}
                  {item.buyersRRP > 0 && item.buyersRRP > item.price && (
                    <span className="buyers-price">{priceAndPointConverter(item.buyersRRP)}</span>
                  )}
                  <PricePerLitre pricePerLitre={item.pricePerLitre} />
                </div>
              )}
            </div>

            {showAddtoCart && (
              <CartButtonWithQuantitySelector
                hit={product}
                itemCode={item.itemCode}
                showQtySelector={false}
                isMiniCart={!showRating}
              />
            )}

            <Zoom product={item} opened={opened} handleOnClose={handleOnClose} />
          </div>
        </>
      )}
    </div>
  );
}
